import React, { useState, useMemo } from "react";
import {
  FaMapMarkerAlt,
  FaRoute,
  FaChevronDown,
  FaChevronUp,
  FaArrowDown,
  FaRupeeSign,
  FaRoad,
  FaCar,
} from "react-icons/fa";
import {
  calculateRouteSegments,
  calculateSegmentFares,
  formatRouteDisplay,
} from "../utils/tripCalculator";

const TripDetailsSummary = ({
  startLocation,
  destination,
  viaCities = [],
  totalDistance,
  baseRate,
  totalFare,
  tripType = "One Way",
  cabType = "Standard",
  className = "",
  showExpanded = false,
  compact = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(showExpanded);

  // Calculate route segments and fare breakdown
  const routeData = useMemo(() => {
    const numericDistance = parseFloat(totalDistance) || 0;
    const numericRate = parseFloat(baseRate) || 12;

    const segments = calculateRouteSegments(
      startLocation,
      destination,
      viaCities,
      numericDistance,
    );
    const fareBreakdown = calculateSegmentFares(segments.segments, numericRate);

    return {
      ...segments,
      fareBreakdown,
      calculatedFare: fareBreakdown.breakdown.totalFare,
      actualFare: parseFloat(totalFare) || fareBreakdown.breakdown.totalFare,
    };
  }, [
    startLocation,
    destination,
    viaCities,
    totalDistance,
    baseRate,
    totalFare,
  ]);

  // Format distance
  const formatDistance = (distance) => {
    if (!distance) return "0";
    return typeof distance === "number" ? distance.toLocaleString() : distance;
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return "0";
    return typeof amount === "number" ? amount.toLocaleString() : amount;
  };

  // Get route display (collapsed)
  const getCollapsedRoute = () => {
    return formatRouteDisplay(startLocation, destination, viaCities, true);
  };

  // Get expanded route with via cities and segment details
  const getExpandedRoute = () => {
    return routeData.routePath.map((city, index) => {
      let type = "via";
      let icon = <FaMapMarkerAlt className="text-blue-500" />;

      if (index === 0) {
        type = "start";
        icon = <FaMapMarkerAlt className="text-green-500" />;
      } else if (index === routeData.routePath.length - 1) {
        type = "destination";
        icon = <FaMapMarkerAlt className="text-red-500" />;
      }

      // Get segment data for the leg starting from this city
      const segmentData = routeData.fareBreakdown?.segments[index - 1] || null;

      return {
        location: city,
        type,
        icon,
        segmentData: index > 0 ? segmentData : null, // Only show segment data for destinations
        isLast: index === routeData.routePath.length - 1,
      };
    });
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Compact mode for booking history
  if (compact) {
    return (
      <div
        className={`trip-details-compact bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-100 ${className}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FaRoute className="text-blue-600" />
            <div>
              <div className="text-sm font-medium text-gray-900">
                {getCollapsedRoute()}
                {viaCities && viaCities.length > 0 && (
                  <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                    +{viaCities.length} via
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-600">
                {totalDistance && `${formatDistance(totalDistance)} km`}
                {totalDistance && baseRate && " • "}
                {baseRate && `₹${formatCurrency(baseRate)}/km`}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm font-bold text-blue-800">
              ₹{formatCurrency(routeData.actualFare)}
            </div>
            <div className="text-xs text-gray-600">{tripType}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`trip-details-summary bg-white rounded-xl shadow-md border border-gray-200 ${className}`}
    >
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <h3 className="text-xl font-bold text-gray-800 flex items-center mb-4">
          <FaCar className="mr-3 text-blue-600" />
          Trip Details Summary
        </h3>

        {/* Trip Type */}
        <div className="mb-4">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            {tripType}
          </span>
          {cabType && (
            <span className="ml-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
              {cabType}
            </span>
          )}
        </div>

        {/* Route Section */}
        <div className="route-section">
          <div
            className="flex items-center justify-between cursor-pointer p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={toggleExpanded}
          >
            <div className="flex items-center flex-1">
              <FaRoute className="mr-3 text-gray-600" />
              <div>
                <div className="font-medium text-gray-900">Route</div>
                <div className="text-sm text-gray-600">
                  {getCollapsedRoute()}
                  {viaCities && viaCities.length > 0 && (
                    <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                      {viaCities.length} via cit
                      {viaCities.length === 1 ? "y" : "ies"}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center text-gray-400">
              {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
            </div>
          </div>

          {/* Expanded Route Details */}
          {isExpanded && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <h4 className="font-semibold text-blue-800 mb-3">
                Full Route Path
              </h4>
              <div className="space-y-4">
                {getExpandedRoute().map((point, index) => (
                  <div key={index}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center flex-1">
                        <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                          {point.icon}
                        </div>
                        <div className="ml-3 flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {point.location}
                            {point.type === "via" && (
                              <span className="ml-2 text-xs text-blue-600">
                                (Via)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {point.segmentData && (
                        <div className="text-right text-xs text-gray-600">
                          <div>
                            {formatDistance(point.segmentData.distance)} km
                          </div>
                          <div>₹{formatCurrency(point.segmentData.fare)}</div>
                        </div>
                      )}
                    </div>
                    {!point.isLast && (
                      <div className="ml-3 mt-2 flex items-center text-xs text-gray-500">
                        <FaArrowDown className="mr-2" />
                        {point.segmentData && (
                          <span>
                            {formatDistance(point.segmentData.distance)} km • ₹
                            {formatCurrency(point.segmentData.ratePerKm)}/km
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TripDetailsSummary;
